// R8-AGG — autocomplete "Servizio" nella riga preventivo: pesca le voci dal listino
// (ricerca SERVER-SIDE con debounce, page_size piccolo → NON scarica le centinaia di
// voci) e, alla selezione, COPIA descrizione + prezzo_unitario nella riga. Prezzo
// copiato, NON legato: nessuna relazione persistente riga→VoceListino (il listino che
// cambia non tocca i preventivi già fatti). L'inserimento manuale resta intatto: se non
// scegli un suggerimento, il testo digitato rimane la descrizione.
//
// Il dropdown è a position:fixed ancorato al rect dell'input: il contenitore della
// tabella ha overflow:hidden (angoli arrotondati) e un dropdown assoluto verrebbe
// tagliato.
import React, { useState, useEffect, useRef } from 'react';
import { listino } from '../utils/api';
import { etichettaSuggerimento, deveCercareListino, etichettaAltre, coloreHex } from './listinoLogic';

// page_size ampio ma non "tutte le voci": basta per sfogliare un mestiere/categoria
// (l'artigiano digita la categoria, es. "Impianti", e vuole vederle tutte), col
// dropdown scrollabile; oltre, un avviso invita ad affinare invece di scaricare tutto.
const PAGE_SIZE = 50;

export default function ServizioAutocomplete({ value, expanded, inputStyle, onText, onPick }) {
  const [suggerimenti, setSuggerimenti] = useState([]);
  const [totale, setTotale] = useState(0);
  const [aperto, setAperto] = useState(false);
  const [attivo, setAttivo] = useState(-1);
  const [pos, setPos] = useState(null);
  const anchorRef = useRef(null);
  const skip = useRef(false);          // dopo una scelta, non riaprire per il cambio di `value`
  const blurTimer = useRef(null);

  // Ricerca server-side con debounce (300ms). page_size 8 = lista corta, mai tutte le voci.
  useEffect(() => {
    if (skip.current) { skip.current = false; return; }
    if (!deveCercareListino(value)) { setSuggerimenti([]); setAperto(false); return; }
    const t = setTimeout(() => {
      listino.list({ search: value.trim(), page_size: PAGE_SIZE })
        .then((res) => {
          const items = res.data?.results || res.data || [];
          setSuggerimenti(items);
          setTotale(typeof res.data?.count === 'number' ? res.data.count : items.length);
          setAperto(items.length > 0);
          setAttivo(-1);
        })
        .catch(() => { setSuggerimenti([]); setTotale(0); setAperto(false); });
    }, 300);
    return () => clearTimeout(t);
  }, [value]);

  // Posizione fixed del dropdown, aggiornata su scroll/resize finché è aperto.
  useEffect(() => {
    if (!aperto) return;
    const aggiorna = () => {
      const el = anchorRef.current;
      if (el) {
        const r = el.getBoundingClientRect();
        setPos({ left: r.left, top: r.bottom, width: r.width });
      }
    };
    aggiorna();
    window.addEventListener('scroll', aggiorna, true);
    window.addEventListener('resize', aggiorna);
    return () => {
      window.removeEventListener('scroll', aggiorna, true);
      window.removeEventListener('resize', aggiorna);
    };
  }, [aperto]);

  const scegli = (voce) => {
    skip.current = true;               // il conseguente cambio di `value` non deve ricercare
    onPick(voce);
    setAperto(false);
    setSuggerimenti([]);
  };

  const onKeyDown = (e) => {
    if (!aperto || suggerimenti.length === 0) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setAttivo((a) => Math.min(suggerimenti.length - 1, a + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setAttivo((a) => Math.max(0, a - 1)); }
    else if (e.key === 'Enter' && attivo >= 0) { e.preventDefault(); scegli(suggerimenti[attivo]); }
    else if (e.key === 'Escape') { setAperto(false); }
  };

  const comuni = {
    value,
    onChange: (e) => onText(e.target.value.slice(0, 500)),
    onKeyDown,
    onFocus: () => { if (suggerimenti.length) setAperto(true); },
    onBlur: () => { blurTimer.current = setTimeout(() => setAperto(false), 150); },
  };

  return (
    <div ref={anchorRef} style={{ position: 'relative' }}>
      {expanded ? (
        <textarea rows={3} {...comuni}
          style={{ ...inputStyle, resize: 'none', minHeight: 60, fontFamily: 'inherit' }}
          placeholder="Descrizione dettagliata del servizio…" />
      ) : (
        <input {...comuni} style={inputStyle} placeholder="Lavorazione… (cerca nel listino)" />
      )}
      {aperto && pos && (
        <div style={{
          position: 'fixed', top: pos.top, left: pos.left, width: pos.width, zIndex: 1200,
          background: 'var(--ds-bg)', border: '1px solid var(--ds-border)',
          borderRadius: 'var(--radius-base)', boxShadow: '0 8px 24px rgba(0,0,0,0.14)',
          maxHeight: 240, overflowY: 'auto', marginTop: 2,
        }}>
          {suggerimenti.map((v, idx) => (
            <div key={v.id}
              onMouseDown={(e) => { e.preventDefault(); scegli(v); }}
              onMouseEnter={() => setAttivo(idx)}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
                padding: '8px 10px', fontSize: 13, cursor: 'pointer',
                background: idx === attivo ? 'var(--chip)' : 'transparent',
              }}>
              <span style={{ color: 'var(--ink)' }}>{etichettaSuggerimento(v)}</span>
              {/* R8-CAT — pallino col colore della categoria (entità) + nome: identificabile a colpo
                  d'occhio come nelle card. Fallback al testo `categoria` per le voci senza entità. */}
              {(v.categoria_nome || v.categoria) && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, color: 'var(--ds-muted)', background: 'var(--chip)', borderRadius: 99, padding: '2px 8px', whiteSpace: 'nowrap' }}>
                  <span style={{ width: 7, height: 7, borderRadius: '50%', background: coloreHex(v.categoria_colore), flexShrink: 0 }} />
                  {v.categoria_nome || v.categoria}
                </span>
              )}
            </div>
          ))}
          {etichettaAltre(totale, suggerimenti.length) && (
            <div style={{ padding: '7px 10px', fontSize: 12, color: 'var(--ds-muted)', borderTop: '1px solid var(--ds-border)', background: 'var(--ds-bg)' }}>
              {etichettaAltre(totale, suggerimenti.length)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
